import React, { useState, useEffect } from 'react';
import {
  PlayIcon,
  StopIcon,
  TrashIcon,
  ArrowPathIcon,
  ClockIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { automationApi } from '@/services/api';

interface AutomationSession {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  isPaperTrading: boolean;
  currentStatus: 'running' | 'stopped' | 'paused' | 'error';
  strategies: string[];
  executionInterval: number;
  maxDailyExecutions: number;
  strategyExecutionMode: string;
  maxDailyVolume: number;
  maxPositionSizePct: number;
  stopLossPct: number | null;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  lastExecutionAt: string | null;
  nextExecutionAt: string | null;
  totalTrades: number;
  profitableTrades: number;
  totalVolume: number;
  totalProfitLoss: number;
  createdAt: string;
  updatedAt: string;
  lastErrorMessage?: string;
  lastErrorAt?: string;
}

interface Execution {
  id: string;
  executionType: 'scheduled' | 'manual';
  startedAt: string;
  completedAt: string | null;
  duration: number | null;
  status: 'completed' | 'failed' | 'running' | 'cancelled';
  strategiesExecuted: number;
  tradesCreated: number;
  totalVolume: number;
  errorMessage: string | null;
}

interface AutomationSessionDetailsProps {
  session: AutomationSession;
  onExecute: (sessionId: string) => void;
  onStart: (sessionId: string) => void;
  onStop: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
}

const AutomationSessionDetails: React.FC<AutomationSessionDetailsProps> = ({
  session,
  onExecute,
  onStart,
  onStop,
  onDelete
}) => {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    performance: true,
    executions: true
  });

  useEffect(() => {
    if (session) {
      loadRecentExecutions();
    }
  }, [session.id]);

  const loadRecentExecutions = async () => {
    try {
      setLoadingExecutions(true);
      const response = await automationApi.getExecutions(session.id, { limit: 10 });
      const executionsData = (response as any).data?.executions || [];
      setExecutions(executionsData);
    } catch (error) {
      console.error('Failed to load executions:', error);
    } finally {
      setLoadingExecutions(false);
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'Unknown';
    
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatNextExecution = (nextExecution: string | null) => {
    if (!nextExecution) return 'Not scheduled';
    
    const now = new Date();
    const next = new Date(nextExecution);
    const diff = next.getTime() - now.getTime();
    
    if (diff < 0) return 'Overdue';
    
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes < 60) return `in ${minutes}m`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `in ${hours}h ${minutes % 60}m`;
    
    const days = Math.floor(hours / 24);
    return `in ${days}d ${hours % 24}h`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
    case 'running':
      return 'text-green-600 bg-green-100';
    case 'stopped':
      return 'text-gray-600 bg-gray-100';
    case 'error':
      return 'text-red-600 bg-red-100';
    case 'paused':
      return 'text-yellow-600 bg-yellow-100';
    case 'completed':
      return 'text-green-600 bg-green-100';
    case 'failed':
      return 'text-red-600 bg-red-100';
    case 'cancelled':
      return 'text-gray-600 bg-gray-100';
    default:
      return 'text-gray-600 bg-gray-100';
    }
  };

  const calculateSuccessRate = () => {
    if (session.totalExecutions === 0) return 0;
    return Math.round((session.successfulExecutions / session.totalExecutions) * 100);
  };

  const calculateWinRate = () => {
    if (session.totalTrades === 0) return 0;
    return Math.round((session.profitableTrades / session.totalTrades) * 100);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{session.name}</h3>
            <div className="flex items-center space-x-3 mt-1">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.currentStatus)}`}>
                {session.currentStatus.toUpperCase()}
              </span>
              {session.isPaperTrading && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-yellow-700 bg-yellow-100">
                  PAPER TRADING
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {session.currentStatus !== 'running' ? (
              <button
                onClick={() => onStart(session.id)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
              >
                <PlayIcon className="h-3 w-3 mr-1" />
                Start
              </button>
            ) : (
              <button
                onClick={() => onStop(session.id)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
              >
                <StopIcon className="h-3 w-3 mr-1" />
                Stop
              </button>
            )}

            <button
              onClick={() => onExecute(session.id)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
              title="Execute Now"
            >
              <ArrowPathIcon className="h-3 w-3 mr-1" />
              Execute
            </button>

            <button
              onClick={() => onDelete(session.id)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
              title="Delete Session"
            >
              <TrashIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {/* Overview Section */}
        <div className="p-4">
          <button
            onClick={() => toggleSection('overview')}
            className="flex items-center justify-between w-full text-left"
          >
            <h4 className="text-sm font-medium text-gray-900">Overview</h4>
            <ChartBarIcon className="h-4 w-4 text-gray-400" />
          </button>

          {expandedSections.overview && (
            <div className="mt-3 space-y-3">
              {session.description && (
                <p className="text-sm text-gray-600">{session.description}</p>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="font-medium text-gray-500">Strategies</dt>
                  <dd className="mt-1 text-gray-900">{session.strategies.length}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Interval</dt>
                  <dd className="mt-1 text-gray-900">Every {session.executionInterval}min</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Execution Mode</dt>
                  <dd className="mt-1 text-gray-900 capitalize">{session.strategyExecutionMode}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Max Daily Volume</dt>
                  <dd className="mt-1 text-gray-900">${session.maxDailyVolume.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Position Limit</dt>
                  <dd className="mt-1 text-gray-900">{session.maxPositionSizePct}%</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Stop Loss</dt>
                  <dd className="mt-1 text-gray-900">
                    {session.stopLossPct ? `${session.stopLossPct}%` : 'None'}
                  </dd>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Next Execution:</span>
                    <span className="text-gray-900">{formatNextExecution(session.nextExecutionAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last Execution:</span>
                    <span className="text-gray-900">{formatDateTime(session.lastExecutionAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created:</span>
                    <span className="text-gray-900">{formatDateTime(session.createdAt)}</span>
                  </div>
                </div>
              </div>

              {session.lastErrorMessage && (
                <div className="p-3 bg-red-50 rounded-md">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Last Error</h3>
                      <div className="mt-1 text-sm text-red-700">{session.lastErrorMessage}</div>
                      {session.lastErrorAt && (
                        <div className="mt-1 text-xs text-red-600">
                          {formatDateTime(session.lastErrorAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Performance Section */}
        <div className="p-4">
          <button
            onClick={() => toggleSection('performance')}
            className="flex items-center justify-between w-full text-left"
          >
            <h4 className="text-sm font-medium text-gray-900">Performance Metrics</h4>
            <ChartBarIcon className="h-4 w-4 text-gray-400" />
          </button>

          {expandedSections.performance && (
            <div className="mt-3">
              <div className="grid grid-cols-2 gap-4">
                {/* Execution Stats */}
                <div className="bg-blue-50 p-3 rounded-md">
                  <div className="text-xs font-medium text-blue-900 mb-2">Execution Stats</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700">Total:</span>
                      <span className="font-medium text-blue-900">{session.totalExecutions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Success Rate:</span>
                      <span className="font-medium text-blue-900">{calculateSuccessRate()}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Failed:</span>
                      <span className="font-medium text-blue-900">{session.failedExecutions}</span>
                    </div>
                  </div>
                </div>

                {/* Trading Stats */}
                <div className="bg-green-50 p-3 rounded-md">
                  <div className="text-xs font-medium text-green-900 mb-2">Trading Stats</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-700">Total Trades:</span>
                      <span className="font-medium text-green-900">{session.totalTrades}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Win Rate:</span>
                      <span className="font-medium text-green-900">{calculateWinRate()}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Volume:</span>
                      <span className="font-medium text-green-900">${session.totalVolume.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profit/Loss */}
              <div className="mt-4">
                <div className={`p-3 rounded-md ${session.totalProfitLoss >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${session.totalProfitLoss >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                      Total P&L:
                    </span>
                    <span className={`text-lg font-bold ${session.totalProfitLoss >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                      {session.totalProfitLoss >= 0 ? '+' : ''}${session.totalProfitLoss.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Recent Executions */}
        <div className="p-4">
          <button
            onClick={() => toggleSection('executions')}
            className="flex items-center justify-between w-full text-left"
          >
            <h4 className="text-sm font-medium text-gray-900">Recent Executions</h4>
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  loadRecentExecutions();
                }}
                disabled={loadingExecutions}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <ArrowPathIcon className={`h-3 w-3 ${loadingExecutions ? 'animate-spin' : ''}`} />
              </button>
              <ClockIcon className="h-4 w-4 text-gray-400" />
            </div>
          </button>

          {expandedSections.executions && (
            <div className="mt-3">
              {loadingExecutions ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  <p className="mt-1 text-xs text-gray-500">Loading executions...</p>
                </div>
              ) : executions.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No executions yet
                </div>
              ) : (
                <div className="space-y-2">
                  {executions.slice(0, 5).map((execution) => (
                    <div key={execution.id} className="border border-gray-200 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(execution.status)}`}>
                            {execution.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            {execution.executionType === 'manual' ? 'Manual' : 'Scheduled'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDuration(execution.duration)}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Strategies:</span>
                          <span className="ml-1 font-medium">{execution.strategiesExecuted}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Trades:</span>
                          <span className="ml-1 font-medium">{execution.tradesCreated}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Volume:</span>
                          <span className="ml-1 font-medium">${execution.totalVolume.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        {formatDateTime(execution.startedAt)}
                      </div>

                      {execution.errorMessage && (
                        <div className="mt-2 text-xs text-red-600">
                          Error: {execution.errorMessage}
                        </div>
                      )}
                    </div>
                  ))}

                  {executions.length > 5 && (
                    <div className="text-center pt-2">
                      <span className="text-xs text-gray-500">
                        Showing 5 of {executions.length} executions
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomationSessionDetails; 